import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  ChangeAliasRequest,
  ProfileDetails,
  ScopusIdChangeRequest,
  UpdateProfileRequest,
  WosResearcherIdRequest,
} from '@/module/Dashboard/model/DashboardTypes';

import { getQueryKey } from '@feature/utils';

import * as api from './DashboardApi';

const profileKey = getQueryKey('profile');

export function getProfileDetailsOptions(): UseSuspenseQueryOptions<ProfileDetails> {
  return {
    queryKey: profileKey.as(['current']),
    queryFn: () => api.getProfileDetails(),
  };
}

export function useSyncWosPublications() {
  return useMutation({
    mutationFn: () => api.syncWosPublications(),
  });
}

export function useChangeScopusId() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ScopusIdChangeRequest) => api.changeScopusId(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: profileKey.root(),
      }),
  });
}

export function useAddAlias() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ChangeAliasRequest) => api.addAlias(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: profileKey.root(),
      }),
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdateProfileRequest) => api.updateProfile(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: profileKey.root(),
      }),
  });
}

export function useRemoveAlias(request: ChangeAliasRequest) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.removeAlias(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: profileKey.root(),
      }),
  });
}

export function useAddResearcherId() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: WosResearcherIdRequest) => api.addResearcherId(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: profileKey.root(),
      }),
  });
}

export function useRemoveWosResearcherId(request: WosResearcherIdRequest) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.deleteResearcherId(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: profileKey.root(),
      }),
  });
}
